import { getAdminIntegrationSubscriptions, getIntegrationSubscriptions, getStoreIntegrationSubscriptions } from './getIntegrationSubscriptions';
import { getIntegrations } from './getIntegrations';
import { getIntegrationAllowances } from './getIntegrationAllowances';
import { getSalesChannels } from './getSalesChannels';
import { getFulfillmentProviders } from './getFulfillmentProviders';
import { getIntegrationWarehouses } from './getIntegrationWarehouses';
import { getIntegrationTypes } from './getIntegrationTypes';
import { bulkCreateIntegrationAllowances } from './bulkCreateIntegrationAllowances';
import { bulkUpdateIntegrationAllowances } from './bulkUpdateIntegrationAllowances';
import { scopedApi } from '@/session/scopedClients';
import { GLOBAL_SCOPE, type RequestScope } from '@/session/scopedTransport';
import { EndpointResponse } from '@/types/endpoints';
import type {
  Integration,
  IntegrationCreatePayload,
  IntegrationUpdatePayload,
  IntegrationSubscription,
  IntegrationSubscriptionCreatePayload,
  IntegrationSubscriptionConsentPayload,
} from '@/types/integration';
import { utils } from '@/utils';
import type { ReadRequestOptions, WriteRequestOptions } from '../requestOptions';

export const getIntegration = async (id: number, options: ReadRequestOptions): Promise<EndpointResponse<Integration>> => {
  try {
    const response = await scopedApi(GLOBAL_SCOPE).get<EndpointResponse<Integration>>(`/integration/${id}`, options);
    return response.data;
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

export const createIntegration = async (data: IntegrationCreatePayload, options: WriteRequestOptions): Promise<EndpointResponse<Integration>> => {
  try {
    const response = await scopedApi(GLOBAL_SCOPE).post<EndpointResponse<Integration>>('/integration', data, options);
    return response.data;
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

export const updateIntegration = async (
  { id, data }: { id: number; data: IntegrationUpdatePayload },
  options: WriteRequestOptions
): Promise<EndpointResponse<Integration>> => {
  try {
    const response = await scopedApi(GLOBAL_SCOPE).put<EndpointResponse<Integration>>(`/integration/${id}`, data, options);
    return response.data;
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

export const deleteIntegration = async (id: number, options: WriteRequestOptions): Promise<EndpointResponse<null>> => {
  try {
    const response = await scopedApi(GLOBAL_SCOPE).delete<EndpointResponse<null>>(`/integration/${id}`, options);
    return response.data;
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

export const createIntegrationSubscription = async (
  scope: RequestScope,
  { data }: { data: IntegrationSubscriptionCreatePayload },
  options: WriteRequestOptions
): Promise<EndpointResponse<IntegrationSubscription>> => {
  try {
    const response = await scopedApi(scope).post<EndpointResponse<IntegrationSubscription>>('/integration_subscription', data, options);
    return response.data;
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

export type IntegrationSubscriptionConsentResult = {
  body?: string;
};

export const startIntegrationSubscriptionConsent = async (
  scope: RequestScope,
  {
    id,
    data = {},
    webhookName = 'consent',
  }: {
    id: number;
    data?: IntegrationSubscriptionConsentPayload;
    webhookName?: string;
  },
  options: WriteRequestOptions
): Promise<EndpointResponse<IntegrationSubscriptionConsentResult>> => {
  try {
    const response = await scopedApi(scope).post<EndpointResponse<IntegrationSubscriptionConsentResult>>(
      `/integration_subscription/${id}/webhook/${webhookName}`,
      data,
      options
    );
    return response.data;
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

export {
  getAdminIntegrationSubscriptions,
  getIntegrationSubscriptions,
  getStoreIntegrationSubscriptions,
  getIntegrations,
  getIntegrationAllowances,
  getSalesChannels,
  getFulfillmentProviders,
  getIntegrationWarehouses,
  getIntegrationTypes,
  bulkCreateIntegrationAllowances,
  bulkUpdateIntegrationAllowances,
};
